import { alignChannelFromBackend, getChannelLabel } from 'src/data/channel';
import { PERFORMANCE_EXTRACTION_STATUS, VISIBLE_RESOURCES_STATUSES } from 'src/data/performance';
import { waitForJob } from 'src/graphql/job';
import { queryIntegrationAssetResults, queryIntegrationAssetStatus } from 'src/graphql/performance';
import { promisedWait } from 'src/lib/common';
import { AbortError, AsyncRequestError, ExtractionError } from 'src/lib/errors';
import { captureException } from 'src/lib/sentry';

export const CREATION_POLLING_INTERVAL = 3000;

class CopiesExtractionError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'CopiesExtractionError';
    this.code = code;
    this.message = message;
  }
}

export const isCopiesExtractionError = (error) => error instanceof CopiesExtractionError;

const unwrapErrors = (json) => {
  try {
    return JSON.parse(json);
  } catch (error) {
    captureException(error, {
      extra: {
        source: 'store/performance/utils/waitForCopiesExtraction.js -> unwrapErrors',
        value: json,
      },
    });
    return [];
  }
};

const stringifyErrors = (errors) => errors?.map((e) => e.message).join('\n');

const isAuthError = (errorCode) => errorCode === ADS_EXTRACTION_ERROR.unauthorizedAccount;

const checkKnownCopiesExtractionError = (errors, errorCode, newMessage = null) => {
  const error = errors?.find((e) => e?.code === errorCode);
  if (error) {
    const message = error?.message || newMessage;
    if (isAuthError(error.code)) {
      return new CopiesExtractionError(error.code, message);
    } else {
      return new Error(message);
    }
  }
  return null;
};

const ADS_EXTRACTION_ERROR = {
  unauthorizedAccount: 'pc_unauthorized_account',
  authorizationFailed: 'pc_authorization_failed',
  genericError: 'pc_generic_error',
  pcAlreadyConnected: 'pc_already_connected',
  pcDownloadDataFailed: 'pc_download_data_failed',
};

const KNOWN_ERRORS = [
  {
    code: ADS_EXTRACTION_ERROR.unauthorizedAccount,
    message: 'Failed to fetch data, authentication issue please re-authorize.',
  },
  {
    code: ADS_EXTRACTION_ERROR.authorizationFailed,
    message: 'Authorization failed. Please try again.',
  },
  {
    code: ADS_EXTRACTION_ERROR.genericError,
    message: 'Failed to connect account.',
  },
  {
    code: ADS_EXTRACTION_ERROR.pcAlreadyConnected,
    message: 'Account already connected.',
  },
  {
    code: ADS_EXTRACTION_ERROR.pcDownloadDataFailed,
    message: 'Failed to download data.',
  },
];

export const searchForKnownCopiesExtractionError = (errors) => {
  for (let i = 0; i < KNOWN_ERRORS.length; i += 1) {
    const knownErrorData = KNOWN_ERRORS[i];
    const error = checkKnownCopiesExtractionError(
      errors,
      knownErrorData.code,
      knownErrorData.message
    );
    if (error) {
      return error;
    }
  }

  return null;
};

export const waitForCopiesExtraction = async ({
  dataSource,
  customerId,
  accountId,
  onStatus,
  onConnectionError,
  options = null,
}) => {
  // Query only the status of the request and related fields: errors, completionPercent
  const statusResponses = await queryIntegrationAssetStatus(
    {
      accountId,
      customerId,
      dataSource,
      statuses: VISIBLE_RESOURCES_STATUSES,
    },
    { abortSignal: options?.abortSignal }
  );

  const statusResponse = statusResponses[0];
  if (!statusResponse) {
    onStatus({ status: PERFORMANCE_EXTRACTION_STATUS.nullableResults });
    return;
  }

  // If request status was successful - query the actual text variations
  if (statusResponse.status === PERFORMANCE_EXTRACTION_STATUS.connected) {
    const errors = unwrapErrors(statusResponse.errors);
    if (onConnectionError && errors[0]) {
      onConnectionError(errors[0]);
    }
    onStatus({ completionPercent: 100, status: statusResponse.status });
    return queryIntegrationAssetResults(
      {
        accountId,
        customerId,
        dataSource,
        statuses: VISIBLE_RESOURCES_STATUSES,
      },
      { abortSignal: options?.abortSignal }
    );
  }

  // If creation failed - throw returned error upwards
  if (statusResponse.status === PERFORMANCE_EXTRACTION_STATUS.error) {
    const errors = unwrapErrors(statusResponse.errors);

    // Notify the caller about the error status BEFORE throwing
    onStatus({ status: PERFORMANCE_EXTRACTION_STATUS.error });

    const knownError = searchForKnownCopiesExtractionError(errors);
    if (knownError) {
      throw knownError;
    }

    const errorsText =
      stringifyErrors(errors) ||
      `${getChannelLabel(
        alignChannelFromBackend(dataSource)
      )} connection request failed with an unknown error`;
    throw new AsyncRequestError(errorsText);
  }

  // Notify the caller about the current status
  onStatus({
    status: statusResponse.status,
    estimatedDuration: statusResponse.estimatedActionsDuration,
    completionPercent:
      statusResponse.completionPercent || statusResponse.completionPercent === 0
        ? Math.min(99, statusResponse.completionPercent)
        : null,
  });

  // Wait sometime and check again
  await promisedWait(CREATION_POLLING_INTERVAL);

  // Stop the process if the request was aborted while we were waiting
  if (options?.abortSignal?.aborted) {
    throw new AbortError();
  }

  return waitForCopiesExtraction({
    dataSource,
    customerId,
    accountId,
    onStatus,
    onConnectionError,
    options,
  });
};

export const waitForExtraction = ({ id, options = null }) => {
  return waitForJob(id, {
    abortSignal: options?.abortSignal,
    pollingInterval: CREATION_POLLING_INTERVAL,
  }).catch((error) => {
    throw new ExtractionError(error.message || 'Failed to extract', error.code);
  });
};
